const BRAZIL_STATES = {
  AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia',
  CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás',
  MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais',
  PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí',
  RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul',
  RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo',
  SE: 'Sergipe', TO: 'Tocantins',
};

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variantId;
    this.input = this.querySelector('#shipping-cep');
    this.button = this.querySelector('.shipping-calculator__button');
    this.results = this.querySelector('.shipping-calculator__results');

    this.input.addEventListener('input', () => this.formatCep());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.calculate();
    });
    this.button.addEventListener('click', () => this.calculate());
  }

  formatCep() {
    let value = this.input.value.replace(/\D/g, '').slice(0, 8);
    if (value.length > 5) {
      value = value.slice(0, 5) + '-' + value.slice(5);
    }
    this.input.value = value;
  }

  async calculate() {
    const cep = this.input.value.replace(/\D/g, '');

    if (cep.length !== 8) {
      this.showError('Digite um CEP válido com 8 dígitos.');
      return;
    }

    this.showLoading();

    // Guarda o estado real do carrinho para restaurar depois,
    // independentemente do que acontecer no cálculo.
    let originalItems = null;

    try {
      const address = await this.lookupAddress(cep);

      originalItems = await this.snapshotCart();
      await this.isolateCartToThisProduct();

      const rates = await this.fetchRates(cep, address);
      this.showRates(rates);
    } catch (err) {
      this.showError('Não foi possível calcular o frete. Tente novamente.');
      console.error(err);
    } finally {
      // Restaura o carrinho original mesmo se algo falhar acima.
      if (originalItems !== null) {
        await this.restoreCart(originalItems);
      }
    }
  }

  async lookupAddress(cep) {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();

    if (data.erro) {
      throw new Error('CEP não encontrado');
    }

    const stateName = BRAZIL_STATES[data.uf] || data.uf;
    return { province: stateName, city: data.localidade };
  }

  // Salva os itens atuais do carrinho (id, quantidade e properties)
  // para conseguir recolocá-los depois exatamente como estavam.
  async snapshotCart() {
    const cart = await fetch('/cart.js').then((r) => r.json());
    return cart.items.map((item) => ({
      id: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {},
    }));
  }

  // Esvazia o carrinho e adiciona SOMENTE o produto desta página,
  // com quantidade 1, para que o frete calculado reflita só esse item.
  async isolateCartToThisProduct() {
    await fetch('/cart/clear.js', { method: 'POST' });

    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] }),
    });

    if (!addResponse.ok) {
      throw new Error('Não foi possível preparar o cálculo de frete');
    }
  }

  // Limpa o carrinho temporário e devolve os itens originais.
  async restoreCart(originalItems) {
    try {
      await fetch('/cart/clear.js', { method: 'POST' });

      if (originalItems.length > 0) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: originalItems }),
        });
      }
    } catch (err) {
      // Se a restauração falhar, é importante saber — o carrinho do
      // cliente pode ter ficado incorreto.
      console.error('Falha ao restaurar o carrinho original:', err);
    }
  }

  buildParams(cep, address) {
    return new URLSearchParams({
      'shipping_address[zip]': cep,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': address.province,
      'shipping_address[city]': address.city || '',
    });
  }

  async fetchRates(cep, address) {
    const params = this.buildParams(cep, address);

    const prepareResponse = await fetch(`/cart/prepare_shipping_rates.json?${params}`, {
      method: 'POST',
    });

    if (!prepareResponse.ok) {
      throw new Error('Falha ao preparar cálculo de frete');
    }

    return this.pollRates(params);
  }

  async pollRates(params, attempt = 0) {
    if (attempt > 10) {
      throw new Error('Tempo esgotado ao calcular frete');
    }

    const response = await fetch(`/cart/async_shipping_rates.json?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar taxas de frete');
    }

    const text = await response.text();
    const data = text ? JSON.parse(text) : null;

    if (!data || !data.shipping_rates) {
      await new Promise((resolve) => setTimeout(resolve, 700));
      return this.pollRates(params, attempt + 1);
    }

    return data.shipping_rates;
  }

  showLoading() {
    this.results.hidden = false;
    this.results.innerHTML = '<p class="shipping-calculator__loading">Calculando frete...</p>';
  }

  showError(message) {
    this.results.hidden = false;
    this.results.innerHTML = `<p class="shipping-calculator__error">${message}</p>`;
  }

  showRates(rates) {
    this.results.hidden = false;

    if (!rates.length) {
      this.results.innerHTML =
        '<p class="shipping-calculator__error">Não encontramos opções de frete para este CEP.</p>';
      return;
    }

    const html = rates
      .map((rate) => {
        const price = (parseFloat(rate.price) || 0).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        });
        return `
          <div class="shipping-calculator__rate">
            <div>
              <span class="shipping-calculator__rate-name">${rate.name}</span>
              ${
                rate.delivery_range && rate.delivery_range.length
                  ? `<span class="shipping-calculator__rate-days">${this.formatDeliveryRange(rate.delivery_range)}</span>`
                  : ''
              }
            </div>
            <strong>${price === 'R$ 0,00' ? 'Grátis' : price}</strong>
          </div>
        `;
      })
      .join('');

    this.results.innerHTML = html;
  }

  // Corrige o bug de fuso: interpreta a data como local (não UTC),
  // evitando que o prazo apareça um dia a menos.
  formatDeliveryRange(range) {
    try {
      const [min, max] = range.map((d) => {
        const [year, month, day] = d.split('T')[0].split('-');
        return new Date(year, month - 1, day);
      });
      const options = { day: '2-digit', month: '2-digit' };
      return `Chega entre ${min.toLocaleDateString('pt-BR', options)} e ${max.toLocaleDateString('pt-BR', options)}`;
    } catch {
      return '';
    }
  }
}

customElements.define('shipping-calculator', ShippingCalculator);